import React, { useState } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'

const { FiUser, FiBell, FiMoon, FiDatabase, FiDownload, FiTrash2, FiSettings, FiShield, FiHelpCircle } = FiIcons

const Settings = () => {
  const [settings, setSettings] = useState({
    notifications: true,
    darkMode: false,
    reminderTime: '09:00',
    dataBackup: true,
    soundEnabled: true,
    weekStartsOn: 'monday'
  })

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  const exportData = () => {
    const data = {
      habits: [],
      progress: [],
      settings: settings,
      exportDate: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'pattrn-habit-data.json'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const clearAllData = () => {
    if (confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      console.log('Data cleared')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            <SafeIcon icon={FiSettings} className="text-2xl" />
            <h1 className="text-4xl md:text-5xl font-bold">Settings</h1>
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Customize your habit tracking experience to match your preferences
          </p>
        </motion.div>

        <div className="space-y-8">
          {/* Profile Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 text-white">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <SafeIcon icon={FiUser} className="text-xl" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Profile Settings</h2>
                  <p className="text-blue-100">Manage your personal information</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Name
                  </label>
                  <input
                    type="text"
                    defaultValue="John Doe"
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    defaultValue="john@example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio
                </label>
                <textarea
                  rows="3"
                  placeholder="Tell us about yourself and your goals..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm resize-none"
                />
              </div>

              <button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-2xl font-medium hover:shadow-lg transition-all duration-300">
                Save Profile
              </button>
            </div>
          </motion.div>

          {/* Notifications Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <SafeIcon icon={FiBell} className="text-xl" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Notification Settings</h2>
                  <p className="text-green-100">Configure alerts and reminders</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-xl">
                    <SafeIcon icon={FiBell} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Daily Reminders</p>
                    <p className="text-sm text-gray-600">Get reminded to complete your habits</p>
                  </div>
                </div>
                <button
                  onClick={() => handleSettingChange('notifications', !settings.notifications)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.notifications ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.notifications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-xl">
                    <SafeIcon icon={FiSettings} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Sound Effects</p>
                    <p className="text-sm text-gray-600">Play sounds for actions and achievements</p>
                  </div>
                </div>
                <button
                  onClick={() => handleSettingChange('soundEnabled', !settings.soundEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.soundEnabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.soundEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reminder Time
                  </label>
                  <input
                    type="time"
                    value={settings.reminderTime}
                    onChange={(e) => handleSettingChange('reminderTime', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/50 backdrop-blur-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Week Starts On
                  </label>
                  <select
                    value={settings.weekStartsOn}
                    onChange={(e) => handleSettingChange('weekStartsOn', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/50 backdrop-blur-sm"
                  >
                    <option value="sunday">Sunday</option>
                    <option value="monday">Monday</option>
                  </select>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Appearance Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-purple-500 to-violet-600 p-6 text-white">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <SafeIcon icon={FiMoon} className="text-xl" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Appearance</h2>
                  <p className="text-purple-100">Customize your visual experience</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-xl">
                    <SafeIcon icon={FiMoon} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Dark Mode</p>
                    <p className="text-sm text-gray-600">Switch to dark theme (Coming Soon)</p>
                  </div>
                </div>
                <button
                  onClick={() => handleSettingChange('darkMode', !settings.darkMode)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.darkMode ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                  disabled
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.darkMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Data & Privacy Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-orange-500 to-red-600 p-6 text-white">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <SafeIcon icon={FiShield} className="text-xl" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Data & Privacy</h2>
                  <p className="text-orange-100">Manage your data and privacy settings</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-orange-100 rounded-xl">
                    <SafeIcon icon={FiDatabase} className="text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Auto Backup</p>
                    <p className="text-sm text-gray-600">Automatically backup your data</p>
                  </div>
                </div>
                <button
                  onClick={() => handleSettingChange('dataBackup', !settings.dataBackup)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.dataBackup ? 'bg-orange-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.dataBackup ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  onClick={exportData}
                  className="flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-2xl font-medium hover:shadow-lg transition-all duration-300"
                >
                  <SafeIcon icon={FiDownload} />
                  <span>Export Data</span>
                </button>
                <button
                  onClick={clearAllData}
                  className="flex items-center justify-center space-x-2 bg-gradient-to-r from-red-600 to-pink-600 text-white px-6 py-4 rounded-2xl font-medium hover:shadow-lg transition-all duration-300"
                >
                  <SafeIcon icon={FiTrash2} />
                  <span>Clear Data</span>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Database Connection Status */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-yellow-500 to-orange-600 p-6 text-white">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <SafeIcon icon={FiDatabase} className="text-xl" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Database Connection</h2>
                  <p className="text-yellow-100">Current connection status</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-6">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-yellow-100 rounded-xl">
                    <SafeIcon icon={FiHelpCircle} className="text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-medium text-yellow-800 mb-2">
                      Currently using mock data
                    </p>
                    <p className="text-sm text-yellow-700 mb-4">
                      To connect to your Supabase database, update the credentials in <code className="bg-yellow-200 px-2 py-1 rounded">src/lib/supabase.js</code>
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-yellow-700">
                      <span>• Real-time sync</span>
                      <span>• Cloud backup</span>
                      <span>• Multi-device access</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Settings